/**
 * Top-level parser for AutoCAD SHX Unicode Font binaries.
 *
 * Supported formats:
 *   - `AutoCAD-2000 unifont 2.0` (the current AutoCAD format)
 *   - `AutoCAD-86 unifont 1.0`   (the 1986 DOS format, still used by
 *                                 GOST / CIS industry fonts)
 *
 * File layout (big picture — see Autodesk SHP/SHX reference):
 *
 *   v2.0 header:
 *   +----------------------------------+
 *   | "AutoCAD-2000 unifont 2.0\r\n"   | ASCII header
 *   | 0x1A 0x00                        | (terminator with trailing null)
 *   +----------------------------------+
 *
 *   v1.0 header:
 *   +----------------------------------+
 *   | "AutoCAD-86 unifont 1.0\r\n"     | ASCII header
 *   | 0x1A                             | (terminator, NO trailing null)
 *   | uint16 LE  shape_count           | number of shape records
 *   +----------------------------------+
 *
 *   Common body (both versions):
 *   +----------------------------------+
 *   | uint16 LE  shape number (0)      | Font header record
 *   | uint16 LE  def length            |
 *   |  def bytes:                      |
 *   |   ASCII name, null-terminated    |
 *   |   uint8  above  (ascender)       |
 *   |   uint8  below  (descender)      |
 *   |   uint8  mode   (0 horiz, 2 dual)|
 *   |   uint8  encoding (0 = unicode)  |
 *   |   uint8  embeddedCount           |
 *   |   uint8  zero (pad)              |
 *   |   0x00                           |
 *   +----------------------------------+
 *   | uint16 LE  codepoint             | Glyph 1
 *   | uint16 LE  def length            |
 *   |  def bytes (shape opcodes)       |
 *   +----------------------------------+
 *   ... repeated until codepoint == 0 ...
 *
 * All numeric fields outside the def bytes are little-endian.
 */

import {
  Cursor,
  isCursorTruncationError,
  type CursorTruncationError,
} from "./cursor";
import { interpretShape, type InterpreterError } from "./shapeInterpreter";
import type {
  ShxFontEncoding,
  ShxFontMode,
  ShxFormatVersion,
  ShxGlyph,
  ShxParseError,
  ShxParseErrorCode,
  ShxParseResult,
} from "./types";

const MAGIC_UNIFONT_2_0 = "AutoCAD-2000 unifont 2.0";
const MAGIC_UNIFONT_1_0 = "AutoCAD-86 unifont 1.0";
const MAGIC_BIGFONT = "AutoCAD-86 bigfont";
const MAGIC_SHAPES_1_0 = "AutoCAD-86 shapes";

/** Maximum reasonable header read length — AutoCAD headers are <=32 bytes. */
const MAGIC_READ_LEN = 64;

function err(
  code: ShxParseErrorCode,
  message: string,
  offset?: number,
): ShxParseResult {
  const error: ShxParseError =
    offset === undefined ? { code, message } : { code, message, offset };
  return { ok: false, error };
}

function isInterpreterError(value: unknown): value is InterpreterError {
  if (typeof value !== "object" || value === null) return false;
  const code = (value as { code?: unknown }).code;
  return (
    code === "bad-shape" ||
    code === "subshape-cycle" ||
    code === "unknown-opcode" ||
    code === "truncated"
  );
}

function classifyInterpreterCode(
  code: InterpreterError["code"],
): ShxParseErrorCode {
  return code;
}

/**
 * Parse a SHX font binary. Never throws — all failure modes surface as
 * `{ ok: false, error }` so callers (e.g. UI `FontManagerDialog`) can
 * decide whether to show a toast, drop the file, or store the raw
 * bytes for diagnostics.
 */
export function parseShxFont(bytes: Uint8Array): ShxParseResult {
  if (bytes.byteLength < 32) {
    return err(
      "truncated",
      `SHX buffer too short (${bytes.byteLength} bytes)`,
      0,
    );
  }

  const cursor = new Cursor(bytes);

  // --- Magic / version check -----------------------------------------
  const magic = cursor.readNullTerminatedAscii(MAGIC_READ_LEN);

  let formatVersion: ShxFormatVersion;
  if (magic.startsWith(MAGIC_UNIFONT_2_0)) {
    formatVersion = 2;
  } else if (magic.startsWith(MAGIC_UNIFONT_1_0)) {
    formatVersion = 1;
  } else if (
    magic.startsWith(MAGIC_BIGFONT) ||
    magic.startsWith(MAGIC_SHAPES_1_0)
  ) {
    // Recognised but unsupported — these use a fundamentally different
    // shape grammar (bigfont has a 2-byte codepage prefix, shapes is
    // the pre-unifont non-Unicode format). Not in scope.
    return err(
      "unsupported-version",
      `Recognised but unsupported SHX variant "${magic.trim()}" — only "AutoCAD-2000 unifont 2.0" and "AutoCAD-86 unifont 1.0" are supported`,
      0,
    );
  } else {
    return err(
      "bad-magic",
      `Not a recognised SHX file (header: "${magic.slice(0, 32).trim()}")`,
      0,
    );
  }

  // --- Header trailer / shape-count skip -----------------------------
  // v2.0 layout:  magic \r\n \x1A \x00   (four trailer bytes)
  // v1.0 layout:  magic \r\n \x1A        (three trailer bytes) followed
  //               by  uint16 LE shape_count  that we read-and-discard
  //
  // `readNullTerminatedAscii` stops AT the first 0x00 without consuming
  // it, so for v2.0 we still need to scan past the final null (and the
  // potentially preceding \r\n\x1A). For v1.0 there is no null byte at
  // all — we rewind to absolute offset and skip the exact header size.
  if (formatVersion === 2) {
    const trailerErr = skipV2Trailer(cursor);
    if (trailerErr) return trailerErr;
  } else {
    const headerErr = seekV1HeaderBody(cursor, bytes);
    if (headerErr) return headerErr;
  }

  // --- Optional per-file header byte ---------------------------------
  // Both versions occasionally have a stray alignment / pad byte
  // between the trailer and the first shape record (depends on the
  // writer). Peek shape-0 uint16; if it is not zero, consume one byte
  // and try again.
  try {
    const shape0Num = peekUint16LE(cursor);
    if (shape0Num !== 0) {
      cursor.skipBytes(1);
    }
  } catch (thrown: unknown) {
    if (isCursorTruncationError(thrown)) {
      return truncatedResult(thrown);
    }
    throw thrown;
  }

  // --- Font header (shape 0) -----------------------------------------
  let headerShapeNum: number;
  let headerLen: number;
  let headerBytes: Uint8Array;
  try {
    headerShapeNum = cursor.readUint16LE();
    headerLen = cursor.readUint16LE();
    if (headerShapeNum !== 0) {
      return err(
        "bad-shape",
        `expected shape 0 (font header), got ${headerShapeNum}`,
        cursor.position(),
      );
    }
    if (headerLen === 0) {
      return err(
        "bad-shape",
        "font header record has zero length",
        cursor.position(),
      );
    }
    headerBytes = cursor.readBytes(headerLen);
  } catch (thrown: unknown) {
    if (isCursorTruncationError(thrown)) {
      return truncatedResult(thrown);
    }
    throw thrown;
  }

  const header = parseFontHeader(headerBytes);
  if (!header.ok) {
    return err(header.error.code, header.error.message, cursor.position());
  }

  // --- Remaining glyphs ----------------------------------------------
  const glyphs = new Map<number, ShxGlyph>();
  try {
    while (cursor.remaining() > 4) {
      const codepoint = cursor.readUint16LE();
      const defLen = cursor.readUint16LE();
      if (codepoint === 0 && defLen === 0) break;
      if (defLen === 0) {
        // Empty glyph record — treat as a zero-advance space.
        glyphs.set(codepoint, {
          codepoint,
          strokes: [],
          advanceX: 0,
          advanceY: 0,
        });
        continue;
      }
      if (cursor.remaining() < defLen) {
        return err(
          "truncated",
          `glyph ${codepoint}: def length ${defLen} exceeds remaining buffer`,
          cursor.position(),
        );
      }
      const defBytes = cursor.readBytes(defLen);
      let glyph: ShxGlyph;
      try {
        glyph = interpretShape(codepoint, defBytes, glyphs, formatVersion, 0);
      } catch (thrown: unknown) {
        if (isInterpreterError(thrown)) {
          return err(
            classifyInterpreterCode(thrown.code),
            thrown.message,
            cursor.position(),
          );
        }
        throw thrown;
      }
      glyphs.set(codepoint, glyph);
    }
  } catch (thrown: unknown) {
    if (isCursorTruncationError(thrown)) {
      return truncatedResult(thrown);
    }
    throw thrown;
  }

  return {
    ok: true,
    font: {
      name: header.value.name,
      above: header.value.above,
      below: header.value.below,
      mode: header.value.mode,
      encoding: header.value.encoding,
      embeddedCount: header.value.embeddedCount,
      glyphs,
      rawBytes: bytes,
      formatVersion,
    },
  };
}

/**
 * Skip the v2.0 header trailer.
 *
 * After the ASCII magic header AutoCAD writes `\r\n` then `0x1A 0x00`
 * as an MS-DOS file-end marker. The exact byte layout of the trailer
 * varies slightly between releases; skip forward to the first `0x00`,
 * consume it, and assume the font header record starts immediately
 * after. Some writers leave a dangling `0x1A` between the trailer and
 * the first shape record; consume it opportunistically.
 */
function skipV2Trailer(cursor: Cursor): ShxParseResult | null {
  cursor.skipWhile((b) => b !== 0x00);
  if (cursor.remaining() === 0) {
    return err("truncated", "unexpected EOF after magic", cursor.position());
  }
  cursor.skipBytes(1); // consume the 0x00
  if (cursor.remaining() > 0) {
    const peek = cursor.peekBytes(1);
    if (peek.length === 1 && peek[0] === 0x1a) {
      cursor.skipBytes(1);
    }
  }
  return null;
}

/**
 * Position the cursor at the start of the shape records in a v1.0
 * file.
 *
 * The v1.0 layout is:
 *   <magic "AutoCAD-86 unifont 1.0"> <0x0D 0x0A 0x1A> <uint16 LE shape_count>
 *
 * Because v1.0 does not write a null terminator after the magic, the
 * 64-byte null-terminated read in `readNullTerminatedAscii` either
 * overshoots (consuming shape-count and beyond) or reaches the buffer
 * limit without finding a null. Either way the cursor is out of
 * position, so we reset to the start of the buffer and step past the
 * exact 25-byte prefix (22 ASCII + `\r\n\x1A`) plus the 2-byte shape
 * count.
 */
function seekV1HeaderBody(
  cursor: Cursor,
  bytes: Uint8Array,
): ShxParseResult | null {
  // Magic "AutoCAD-86 unifont 1.0" is exactly 22 bytes.
  const MAGIC_V1_LEN = MAGIC_UNIFONT_1_0.length; // 22
  // Plus \r\n\x1A = 3 bytes.
  const TRAILER_LEN = 3;
  // Plus uint16 LE shape_count = 2 bytes.
  const SHAPE_COUNT_LEN = 2;
  const BODY_OFFSET = MAGIC_V1_LEN + TRAILER_LEN + SHAPE_COUNT_LEN;

  if (bytes.byteLength < BODY_OFFSET) {
    return err(
      "truncated",
      `v1.0 header truncated: need ${BODY_OFFSET} bytes, have ${bytes.byteLength}`,
      0,
    );
  }

  // Validate the trailer bytes are what we expect. If a writer used a
  // different line ending (just \n, for example) fail loudly rather
  // than silently parse garbage.
  const cr = bytes[MAGIC_V1_LEN] as number;
  const lf = bytes[MAGIC_V1_LEN + 1] as number;
  const sub = bytes[MAGIC_V1_LEN + 2] as number;
  if (cr !== 0x0d || lf !== 0x0a || sub !== 0x1a) {
    return err(
      "bad-magic",
      `v1.0 header trailer not "\\r\\n\\x1A" (got ${cr.toString(16)} ${lf.toString(16)} ${sub.toString(16)})`,
      MAGIC_V1_LEN,
    );
  }

  cursor.seek(BODY_OFFSET);
  return null;
}

function peekUint16LE(cursor: Cursor): number {
  const bytes = cursor.peekBytes(2);
  if (bytes.length < 2) {
    throw {
      code: "truncated",
      offset: cursor.position(),
      message: "unexpected EOF peeking uint16",
    } satisfies CursorTruncationError;
  }
  return (bytes[0] as number) | ((bytes[1] as number) << 8);
}

function truncatedResult(error: CursorTruncationError): ShxParseResult {
  return err("truncated", error.message, error.offset);
}

type FontHeader = {
  name: string;
  above: number;
  below: number;
  mode: ShxFontMode;
  encoding: ShxFontEncoding;
  embeddedCount: number;
};

type HeaderResult =
  | { ok: true; value: FontHeader }
  | { ok: false; error: ShxParseError };

/**
 * Parse the font-header record (shape 0) def bytes.
 *
 * Layout:
 *   ASCII name, null-terminated
 *   uint8 above
 *   uint8 below
 *   uint8 mode    (0 horizontal, 2 dual)
 *   uint8 encoding (0 unicode, 1 MIF, 2 shape-file)
 *   uint8 embeddedCount
 *   uint8 reserved (expected 0)
 *   uint8 trailer 0x00
 */
function parseFontHeader(headerBytes: Uint8Array): HeaderResult {
  // Find null terminator for the name.
  let nameEnd = -1;
  for (let i = 0; i < headerBytes.length; i++) {
    if (headerBytes[i] === 0) {
      nameEnd = i;
      break;
    }
  }
  if (nameEnd === -1) {
    return {
      ok: false,
      error: {
        code: "bad-shape",
        message: "font header: name not null-terminated",
      },
    };
  }
  let name = "";
  for (let i = 0; i < nameEnd; i++) {
    name += String.fromCharCode(headerBytes[i] as number);
  }

  const metaOffset = nameEnd + 1;
  // We expect at least 6 bytes of metadata after the name.
  if (headerBytes.length < metaOffset + 6) {
    return {
      ok: false,
      error: {
        code: "bad-shape",
        message: `font header: truncated metadata (${headerBytes.length - metaOffset} of 6 bytes)`,
      },
    };
  }
  const above = headerBytes[metaOffset] as number;
  const below = headerBytes[metaOffset + 1] as number;
  const modeByte = headerBytes[metaOffset + 2] as number;
  const encodingByte = headerBytes[metaOffset + 3] as number;
  const embeddedCount = headerBytes[metaOffset + 4] as number;
  // headerBytes[metaOffset + 5] is the reserved / zero byte — ignored
  // for forward-compatibility.

  const mode: ShxFontMode = modeByte === 2 ? 2 : 0;
  const encoding: ShxFontEncoding =
    encodingByte === 1 ? 1 : encodingByte === 2 ? 2 : 0;

  return {
    ok: true,
    value: {
      name,
      above,
      below,
      mode,
      encoding,
      embeddedCount,
    },
  };
}
